import { Suspense, lazy, useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ThemeProvider, CssBaseline, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import theme from "./theme";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { HelmetProvider } from "react-helmet-async";
import { UserProvider } from "./context/UserContext";
import RequireRole from "./context/RequireRole";
import { ContextProvider } from "./scenes/pages/user/Onboarding/context";

const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const LogoutOnAccess = lazy(() => import("./components/LogoutOnAccess"));

// Layouts
const RootLayout = lazy(() => import("./scenes/RootLayout"));

// My HRA / User
const UserDashboard = lazy(() => import("./scenes/pages/user/UserDashboard"));
const AddExpense = lazy(
  () => import("./scenes/pages/user/expenses/AddExpense"),
);
const Contact = lazy(() => import("./scenes/pages/user/Contact"));
const MFA = lazy(() => import("./scenes/pages/user/MFA"));
const BankAccount = lazy(() => import("./scenes/pages/user/BankAccount"));
const Address = lazy(() => import("./scenes/pages/user/Address"));
const Dependents = lazy(() => import("./scenes/pages/user/Dependents"));
const AddDependent = lazy(() => import("./scenes/pages/user/AddDependent"));
const MedicalInsurance = lazy(
  () => import("./scenes/pages/user/MedicalInsurance"),
);
const HealthInsurance = lazy(
  () => import("./scenes/pages/user/HealthInsurance"),
);
const AuditLog = lazy(() => import("./scenes/pages/user/AuditLog"));
const ConfirmHealthInsurance = lazy(
  () => import("./scenes/pages/user/ConfirmHealthInsurance"),
);
const HealthInsurancePlanDetails = lazy(
  () => import("./scenes/pages/user/HealthInsurancePlanDetails"),
);
const ChangePassword = lazy(() => import("./scenes/pages/user/ChangePassword"));
const VerifyPassword = lazy(
  () => import("./scenes/pages/account/VerifyPassword"),
);
const ChangeEmail = lazy(() => import("./scenes/pages/user/ChangeEmail"));
const UserTextMessages = lazy(
  () => import("./scenes/pages/user/UserTextMessages"),
);
const UserEmailMessages = lazy(
  () => import("./scenes/pages/user/UserEmailMessages"),
);
const PlanDocuments = lazy(() => import("./scenes/pages/user/PlanDocuments"));
const TextToConfirmPremiums = lazy(
  () => import("./scenes/pages/user/ttc-premium/Summary"),
);
const AccountBalances = lazy(
  () => import("./scenes/pages/user/AccountBalance"),
);
const UserPlanDetails = lazy(
  () => import("./scenes/pages/user/plan-details/PlanDetails"),
);
const HsaCompatibilityElection = lazy(
  () => import("./scenes/pages/user/HsaCompatibilityElection"),
);

// const Completion = lazy(() => import("./legacyComponents/componentsV2/FlowSteps/Completion"));
const InsuranceMarketplace = lazy(
  () =>
    import("./scenes/pages/user/Onboarding/components/HRASetupForm/steps/Marketplace"),
);
const EmployeeOnboarding = lazy(() => import("./scenes/pages/user/Onboarding"));
const MedicalExpenses = lazy(
  () => import("./scenes/pages/user/medical-expenses/MedicalExpenses"),
);
const IntroductionForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/IntroductionForm"),
);
const HealthInsuranceForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/HealthInsuranceForm"),
);
const CreateAccountForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/CreateAccountForm"),
);
const HRASetupForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/HRASetupForm"),
);
const InsuranceForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/InsuranceForm"),
);
const PurchaseInsuranceForm = lazy(
  () =>
    import("./scenes/pages/user/Onboarding/components/PurchaseInsuranceForm"),
);
const QSEHRAInsuranceForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/QSEHRAInsuranceForm"),
);
const ReimbursementForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/ReimbursementForm"),
);
const NotificationForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/NotificationForm"),
);
const CompletionForm = lazy(
  () => import("./scenes/pages/user/Onboarding/components/CompletionForm"),
);
const Logout = lazy(() => import("./scenes/pages/account/Logout"));
const ExpenseImageText = lazy(
  () => import("./scenes/pages/user/ExpenseImageText"),
);

// Employer
// const EmployerFlowAuthorizedRepresentative = lazy(() => import("./scenes/pages/employer/employer-flow/Flow_Authorized_Representative"));
const EmployerFlowCompanyDetails = lazy(
  () => import("./scenes/pages/employer/employer-flow/Flow_CompanyDetails"),
);
const EmployerFlowPlans = lazy(
  () => import("./scenes/pages/employer/employer-flow/Flow_Plans"),
);
const EmployerFlowBilling = lazy(
  () => import("./scenes/pages/employer/employer-flow/Flow_Billing"),
);
const EmployerFlowReimbursements = lazy(
  () => import("./scenes/pages/employer/employer-flow/Flow_Reimbursements"),
);
const EmployerFlowDocuments = lazy(
  () => import("./scenes/pages/employer/employer-flow/Flow_Documents"),
);
// const EmployerFlowEnd = lazy(() => import("./scenes/pages/employer/employer-flow/Flow_End"));
const EmployeeSummary = lazy(
  () => import("./scenes/pages/employer/employee-summary/Summary"),
);
const AdministratorSummary = lazy(
  () => import("./scenes/pages/employer/administrator/Summary"),
);
const ReportSummary = lazy(
  () => import("./scenes/pages/employer/report/Summary"),
);
const BillingHistorySummary = lazy(
  () => import("./scenes/pages/employer/BillingHistory"),
);
const AssignPartner = lazy(
  () => import("./scenes/pages/employer/AssignPartner"),
);
const AssignReferral = lazy(
  () => import("./scenes/pages/employer/AssignReferral"),
);
const PremiumSummary = lazy(
  () => import("./scenes/pages/employer/PremiumSummary"),
);
const EmployerPlanDetails = lazy(
  () => import("./scenes/pages/employer/plan-details/PlanDetails"),
);
const AddEmployer = lazy(
  () => import("./scenes/pages/employers/addEmployer/AddEmployer"),
);
const AddAdministrator = lazy(
  () => import("./scenes/pages/employer/administrator/AddAdministrator"),
);
const EmployerClaims = lazy(() => import("./scenes/pages/employer/Claims"));
const EmployerAllocations = lazy(
  () => import("./scenes/pages/employer/Allocations"),
);
const EmployerAccountReports = lazy(
  () => import("./scenes/pages/employer/AccountReports"),
);
const EmployerReports = lazy(() => import("./scenes/pages/employer/Reports"));
const EmployerDocuments = lazy(
  () => import("./scenes/pages/employer/Documents"),
);
const EmployeeCensus = lazy(
  () => import("./scenes/pages/employer/EmployeeCensus"),
);
const EmployerDashboard = lazy(
  () => import("./scenes/pages/employer/employer-dashboard/EmployerDashboard"),
);
const AddEmployee = lazy(
  () => import("./scenes/pages/employer/employer-dashboard/AddEmployee"),
);
const TerminateEmployment = lazy(
  () => import("./scenes/pages/employer/employer-dashboard/TerminateEmployee"),
);

// Partner
const PartnerDashboard = lazy(
  () => import("./scenes/pages/partner/partner-dashboard/PartnerDashboard"),
);
const PartnerLicenses = lazy(
  () => import("./scenes/pages/partner/PartnerLicenses"),
);
const FlowPartner = lazy(
  () => import("./scenes/pages/partner/onboarding/Flow_Partner"),
);
const FlowPartnerDocuments = lazy(
  () => import("./scenes/pages/partner/onboarding/Flow_Documents"),
);
const PartnerDocuments = lazy(
  () => import("./scenes/pages/partner/PartnerDocuments"),
);
const PartnerEmployeeInsurance = lazy(
  () => import("./scenes/pages/partner/PartnerEmployeeInsurance"),
);
const PartnerEmployeeEnrollment = lazy(
  () => import("./scenes/pages/partner/PartnerEmployeeEnrollment"),
);
const ManualEnrollment = lazy(
  () => import("./scenes/pages/partner/ManualEnrollment"),
);
const PartnerEmployees = lazy(
  () => import("./scenes/pages/partner/PartnerEmployees"),
);
const PartnerAdmins = lazy(
  () => import("./scenes/pages/partner/PartnerAdmins"),
);
const AddPartnerAdmin = lazy(
  () => import("./scenes/pages/partner/AddPartnerAdmin"),
);
const PartnerEmployeeOnboarding = lazy(
  () =>
    import("./scenes/pages/partner/employeeOnboarding/PartnerEmployeeOnboarding"),
);

// Referrals
const MyReferrals = lazy(() => import("./scenes/pages/referrals/MyReferrals"));
const AddReferral = lazy(() => import("./scenes/pages/referrals/AddReferral"));

// Users
const UsersDashboard = lazy(
  () => import("./scenes/pages/users/users-dashboard/Summary"),
);

// Employers
const EmployersDashboard = lazy(
  () => import("./scenes/pages/employers/employers-dashboard/Summary"),
);
const CloseEmployer = lazy(
  () => import("./scenes/pages/employers/CloseEmployer"),
);
// Approvals
const ExpenseApprovalV2 = lazy(
  () => import("./scenes/pages/approvals/expense/ExpenseApproval"),
);
const TextImageVerification = lazy(
  () => import("./scenes/pages/approvals/TextImageVerification"),
);

// Ops
const ExpenseApproval = lazy(
  () => import("./scenes/pages/ops/expense/ExpenseApproval"),
);
const TextToConfirmExpenseApproval = lazy(
  () =>
    import("./scenes/pages/ops/textToConfirmExpense/TextToConfirmExpenseApproval"),
);
const TextImageApproval = lazy(
  () => import("./scenes/pages/ops/textImage/ImageApproval"),
);
const InsuranceApproval = lazy(
  () => import("./scenes/pages/ops/insurance/InsuranceApproval"),
);
const Referrals = lazy(() => import("./scenes/pages/ops/Referrals"));
const EmployerInvoices = lazy(
  () => import("./scenes/pages/ops/EmployerInvoices"),
);
const ReleaseHold = lazy(() => import("./scenes/pages/ops/ReleaseHold"));
const SVBACHTransactions = lazy(
  () => import("./scenes/pages/ops/SVBACHTransactions"),
);
const UpdateACHTransaction = lazy(
  () => import("./scenes/pages/ops/UpdateACHTransaction"),
);
const ReimbursementAccountBalance = lazy(
  () => import("./scenes/pages/ops/ReimbursementAccountBalance"),
);
const PlanTemplates = lazy(() => import("./scenes/pages/ops/PlanTemplates"));
const EmailTemplates = lazy(() => import("./scenes/pages/ops/EmailTemplates"));
const MessageTemplates = lazy(
  () => import("./scenes/pages/ops/message/MessageTemplates"),
);
const MessageTemplateEdit = lazy(
  () => import("./scenes/pages/ops/message/MessageTemplateEdit"),
);
const CombineAccounts = lazy(
  () => import("./scenes/pages/ops/CombineAccounts"),
);
const ChangeEmployeeEmail = lazy(
  () => import("./scenes/pages/ops/ChangeEmployeeEmail"),
);
const HealthSherpaPolicies = lazy(
  () => import("./scenes/pages/ops/HealthSherpaPolicies"),
);
// Lives next to AddExpense: it uses the same styles and parts.
const AdminEditExpense = lazy(
  () => import("./scenes/pages/user/expenses/AdminEditExpense"),
);
const Producers = lazy(() => import("./scenes/pages/ops/Producers"));
// Conversations
const Conversations = lazy(
  () => import("./scenes/pages/conversation/Conversations"),
);
const ConversationDetail = lazy(
  () => import("./scenes/pages/conversation/ConversationDetail"),
);
const AddProducer = lazy(() => import("./scenes/pages/ops/AddProducer"));
const AddCarrier = lazy(() => import("./scenes/pages/ops/AddCarrier"));
const AddLicense = lazy(() => import("./scenes/pages/ops/AddLicense"));
const AddStateRegistration = lazy(
  () => import("./scenes/pages/ops/AddStateRegistration"),
);
const AddStateFiling = lazy(() => import("./scenes/pages/ops/AddStateFiling"));
const AddAppointment = lazy(() => import("./scenes/pages/ops/AddAppointment"));
const AddLogin = lazy(() => import("./scenes/pages/ops/AddLogin"));
const InstantlyCampaigns = lazy(
  () => import("./scenes/pages/ops/InstantlyCampaigns"),
);
const InstantlyLeads = lazy(() => import("./scenes/pages/ops/InstantlyLeads"));
const InstantlyEmployers = lazy(
  () => import("./scenes/pages/ops/InstantlyEmployers"),
);
const Reviews = lazy(() => import("./scenes/pages/ops/Reviews"));
const AiExpenseList = lazy(() => import("./scenes/pages/ops/AiExpenseList"));
const AiMessageDetail = lazy(
  () => import("./scenes/pages/ops/AiMessageDetail"),
);
const AiMessageList = lazy(() => import("./scenes/pages/ops/AiMessageList"));
const AiExpenseDetail = lazy(
  () => import("./scenes/pages/ops/AiExpenseDetail"),
);
const UploadHealthSherpaPolicies = lazy(
  () => import("./scenes/pages/ops/UploadHealthSherpaPolicies"),
);
const GetHealthSherpaApplications = lazy(
  () => import("./scenes/pages/ops/GetHealthSherpaApplications"),
);
const GetHealthSherpaApplication = lazy(
  () => import("./scenes/pages/ops/GetHealthSherpaApplication"),
);

// Usage
const MonthlyRevenue = lazy(
  () => import("./scenes/pages/usage/MonthlyRevenue"),
);
const WeeklyEnrollment = lazy(
  () => import("./scenes/pages/usage/WeeklyEnrollment"),
);
const ChannelRevenue = lazy(
  () => import("./scenes/pages/usage/ChannelRevenue"),
);
const EmailCampaignResponses = lazy(
  () => import("./scenes/pages/usage/EmailCampaignResponses"),
);
const AdResponses = lazy(() => import("./scenes/pages/usage/AdResponses"));
const Calculators = lazy(() => import("./scenes/pages/usage/Calculators"));
const HealthSherpaUsage = lazy(
  () => import("./scenes/pages/usage/HealthSherpaUsage"),
);
const EmployerPlans = lazy(() => import("./scenes/pages/usage/EmployerPlans"));
const AdClicks = lazy(() => import("./scenes/pages/usage/AdClicks"));
const IPAddressSummary = lazy(
  () => import("./scenes/pages/usage/IPAddressSummary"),
);
const PublicVisitorHistory = lazy(
  () => import("./scenes/pages/usage/PublicVisitorHistory"),
);
const LearningCenter = lazy(
  () => import("./scenes/pages/usage/LearningCenter"),
);

// Data
const SearchData = lazy(() => import("./scenes/pages/data/search/SearchData"));
const DashboardData = lazy(() => import("./scenes/pages/data/DashboardData"));
const PartnersData = lazy(() => import("./scenes/pages/data/PartnersData"));
const ExpensesData = lazy(() => import("./scenes/pages/data/ExpensesData"));
const TextToConfirmPremiumsData = lazy(
  () => import("./scenes/pages/data/TextToConfirmPremiumsData"),
);
const ExpenseTextsData = lazy(
  () => import("./scenes/pages/data/ExpenseTextsData"),
);
const TextMessagesData = lazy(
  () => import("./scenes/pages/data/TextMessagesData"),
);
const TextResponsesData = lazy(
  () => import("./scenes/pages/data/TextResponsesData"),
);
const ClaimsData = lazy(() => import("./scenes/pages/data/ClaimsData"));
const TransactionsData = lazy(
  () => import("./scenes/pages/data/TransactionsData"),
);
const ManageBankData = lazy(() => import("./scenes/pages/data/ManageBankData"));
const AllBanksData = lazy(() => import("./scenes/pages/data/AllBanksData"));
const NachaTransactionsData = lazy(
  () => import("./scenes/pages/data/NachaTransactionsData"),
);
const HRAAdjustment = lazy(() => import("./scenes/pages/data/HRAAdjustment"));
const HealthSherpaWebhooksData = lazy(
  () => import("./scenes/pages/data/HealthSherpaWebhooksData"),
);

// System
const CronTasks = lazy(() => import("./scenes/pages/system/CronTasks"));
const SystemLogs = lazy(() => import("./scenes/pages/system/SystemLogs"));

// Calculators
const CostOfInsurance = lazy(
  () => import("./scenes/pages/calculators/CostOfInsurance"),
);
const InsurancePlans = lazy(
  () => import("./scenes/pages/calculators/InsurancePlans"),
);
const ACACalculator = lazy(
  () => import("./scenes/pages/calculators/ACACalculator"),
);
const MetalLevelCalculator = lazy(
  () => import("./scenes/pages/calculators/MetalLevelCalculator"),
);
const ICHRACalculator = lazy(
  () => import("./scenes/pages/calculators/ICHRACalculator"),
);
const AvailablePlans = lazy(
  () => import("./scenes/pages/calculators/AvailablePlans"),
);
const AvailablePlanDetails = lazy(
  () => import("./scenes/pages/calculators/AvailablePlanDetails"),
);

// Accounts & Auth
const CreateAccount = lazy(
  () => import("./scenes/pages/account/CreateAccount"),
);
const CreateAccountComplete = lazy(
  () => import("./scenes/pages/account/CreateAccountComplete"),
);
const CreatePartnerAccount = lazy(
  () => import("./scenes/pages/account/CreatePartnerAccount"),
);
const CreateReferralAccount = lazy(
  () => import("./scenes/pages/account/CreateReferralAccount"),
);
const ResetPassword = lazy(
  () => import("./scenes/pages/account/ResetPassword"),
);
const RecoverPassword = lazy(
  () => import("./scenes/pages/account/RecoverPassword"),
);
const VerifyEmployer = lazy(
  () => import("./scenes/pages/account/VerifyEmployer"),
);
const ResendInviteInstructions = lazy(
  () => import("./scenes/pages/account/ResendInviteInstructions"),
);

// Summaries & Shared
const MessageSummary = lazy(() => import("./scenes/pages/message/Summary"));
const HomePage = lazy(() => import("./scenes/pages/home/HomePage"));
const TTCPremiumSummary = TextToConfirmPremiums;

// Support
const Support = lazy(() => import("./scenes/pages/support/index"));

// Error Page
const ErrorPage = lazy(() => import("./scenes/pages/error/ErrorPage"));

const PageLoader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
    }}
  >
    <CircularProgress />
  </Box>
);

const withSuspense = (node) => (
  <Suspense fallback={<PageLoader />}>{node}</Suspense>
);

const render = (Component) => withSuspense(<Component />);

const renderWithWrapper = (WrapperComponent, ChildComponent) =>
  withSuspense(<WrapperComponent>{render(ChildComponent)}</WrapperComponent>);

const renderWithOnboarding = (Component) =>
  withSuspense(
    <ContextProvider>
      <Component />
    </ContextProvider>,
  );

/**
 * The element for a group of admin routes.
 *
 * A route entry with no path and this as its element puts RequireRole around
 * every route in its children, without changing any URL. RequireRole decides
 * before the page mounts, so an admin page is never rendered under an
 * impersonated user's token — which is what produced the 403 on Back.
 */
const guard = (allow) =>
  withSuspense(
    <RequireRole allow={allow}>
      <Outlet />
    </RequireRole>,
  );

const router = createBrowserRouter([
  {
    path: "/",
    element: render(RootLayout),
    errorElement: render(ErrorPage),
    children: [
      { index: true, element: <Navigate to="/login" replace /> },
      { path: "/login", element: renderWithWrapper(LogoutOnAccess, HomePage) },
      { path: "/logout", element: render(Logout) },
      // Public routes that should log out the user
      { path: "/create-account", element: renderWithWrapper(CreateAccount) },
      {
        path: "/create-account/complete",
        element: renderWithWrapper(CreateAccountComplete),
      },
      {
        path: "/create-referral-account",
        element: renderWithWrapper(CreateReferralAccount),
      },
      {
        path: "/create-partner-account",
        element: renderWithWrapper(CreatePartnerAccount),
      },
      {
        path: "/recover-password",
        element: renderWithWrapper(LogoutOnAccess, RecoverPassword),
      },
      {
        path: "/reset-password",
        element: renderWithWrapper(LogoutOnAccess, ResetPassword),
      },
      {
        path: "/verify-password",
        element: renderWithWrapper(LogoutOnAccess, VerifyPassword),
      },
      {
        path: "/contact-us",
        element: renderWithWrapper(LogoutOnAccess, Contact),
      },
      {
        path: "/calculator/cost-of-insurance",
        element: render(CostOfInsurance),
      },
      { path: "/calculator/plans", element: render(InsurancePlans) },
      {
        path: "/calculator/metal-based-ichra-employer",
        element: render(MetalLevelCalculator),
      },
      {
        path: "/calculator/affordable-ichra-employer",
        element: render(ACACalculator),
      },
      {
        path: "/calculator/affordable-ichra-employee",
        element: render(ICHRACalculator),
      },
      { path: "/calculator/available-plans", element: render(AvailablePlans) },
      {
        path: "/calculator/available-plan-details",
        element: render(AvailablePlanDetails),
      },
      { path: "/verify", element: render(VerifyEmployer) },
      {
        path: "/resend",
        element: renderWithWrapper(LogoutOnAccess, ResendInviteInstructions),
      },
      // { path: "/onboarding/manage", element: render(EmployeeOnboarding) },
      // { path: "/onboarding/manage/:userPlanId", element: render(EmployeeOnboarding) },
      // { path: "/onboarding/manage/:userPlanId/:step", element: render(EmployeeOnboarding) },
      {
        path: "/onboarding/add/:userPlanId",
        element: render(EmployeeOnboarding),
      },
      // { path: "/onboarding/add/:userPlanId", element: render(EmployeeOnboarding) },
      // { path: "/onboarding/completion/:userId", element: render(Completion) },
      { path: "/support", element: render(Support) },

      {
        element: render(ProtectedRoute), // ✅ wrap protected section
        children: [
          { path: "/user", element: render(UserDashboard) },
          { path: "/user/dependents", element: render(Dependents) },
          { path: "/user/dependents/add", element: render(AddDependent) },
          {
            path: "/user/dependents/:dependentId",
            element: render(AddDependent),
          },
          {
            path: "/user/expense-image-text/:expenseImageTextId",
            element: render(ExpenseImageText),
          },
          {
            path: "/user/plan-details/:user_id",
            element: render(UserPlanDetails),
          },
          { path: "/add-expense", element: render(AddExpense) },
          { path: "/edit-expense/:expense_id", element: render(AddExpense) },
          { path: "/add-employee/:employerId", element: render(AddEmployee) },
          {
            path: "/add-employee/:employerId/employee/:userId",
            element: render(AddEmployee),
          },
          {
            path: "/terminate-employee/:employerId/employee/:userId",
            element: render(TerminateEmployment),
          },
          { path: "/employer/:employerId", element: render(EmployerDashboard) },
          { path: "/medical-expenses", element: render(MedicalExpenses) },
          { path: "/user-documents", element: render(PlanDocuments) },
          {
            path: "/employee/insurance/marketplace",
            element: render(InsuranceMarketplace),
          },

          // My Hra
          { path: "/address", element: render(Address) },
          { path: "/contact", element: render(Contact) },
          { path: "/mfa", element: render(MFA) },
          { path: "/bank-account", element: render(BankAccount) },
          { path: "/medical-insurance", element: render(MedicalInsurance) },
          { path: "/health-insurance", element: render(HealthInsurance) },

          // { path: "/health-insurance/:userId", element: render(HealthInsurance) },
          {
            path: "/health-insurance/plan-details",
            element: render(HealthInsurancePlanDetails),
          },
          {
            path: "/health-insurance/confirm",
            element: render(ConfirmHealthInsurance),
          },
          { path: "/change-email", element: render(ChangeEmail) },
          { path: "/change-password", element: render(ChangePassword) },
          { path: "/user-text-messages", element: render(UserTextMessages) },
          { path: "/user-emails", element: render(UserEmailMessages) },
          {
            path: "/user/text-to-confirm-premiums",
            element: render(TextToConfirmPremiums),
          },
          { path: "/user/account-balances", element: render(AccountBalances) },
          {
            path: "/user/account-reports",
            element: withSuspense(<EmployerAccountReports employeeView />),
          },
          {
            path: "/user/hsa-compatibility-election",
            element: render(HsaCompatibilityElection),
          },
          {
            path: "/employee-flow/introduction",
            element: renderWithOnboarding(IntroductionForm),
          },
          {
            path: "/employee-flow/health-insurance",
            element: renderWithOnboarding(HealthInsuranceForm),
          },
          {
            path: "/employee-flow/create-account",
            element: renderWithOnboarding(CreateAccountForm),
          },
          {
            path: "/employee-flow/hra-setup",
            element: renderWithOnboarding(HRASetupForm),
          },
          {
            path: "/employee-flow/insurance",
            element: renderWithOnboarding(InsuranceForm),
          },
          {
            path: "/employee-flow/purchase-insurance",
            element: renderWithOnboarding(PurchaseInsuranceForm),
          },
          {
            path: "/employee-flow/qsehra-insurance",
            element: renderWithOnboarding(QSEHRAInsuranceForm),
          },
          {
            path: "/employee-flow/reimbursements",
            element: renderWithOnboarding(ReimbursementForm),
          },
          {
            path: "/employee-flow/notification",
            element: renderWithOnboarding(NotificationForm),
          },
          {
            path: "/employee-flow/completion",
            element: renderWithOnboarding(CompletionForm),
          },

          // Employer
          { path: "/employer-dashboard", element: render(EmployerDashboard) },
          {
            path: "/employer/plan-details/:employer_id",
            element: render(EmployerPlanDetails),
          },
          {
            path: "/employer-flow/company-details",
            element: render(EmployerFlowCompanyDetails),
          },
          { path: "/employer-flow/plans", element: render(EmployerFlowPlans) },
          {
            path: "/employer-flow/reimbursements",
            element: render(EmployerFlowReimbursements),
          },
          {
            path: "/employer-flow/billing",
            element: render(EmployerFlowBilling),
          },
          {
            path: "/employer-flow/documents",
            element: render(EmployerFlowDocuments),
          },
          // { path: "/employer-flow/end", element: render(EmployerFlowEnd) },
          {
            path: "/employer/administrators",
            element: render(AdministratorSummary),
          },
          {
            path: "/employer/:employerId/administrator/add",
            element: render(AddAdministrator),
          },
          { path: "/employer-claims", element: render(EmployerClaims) },
          {
            path: "/employer-allocations",
            element: render(EmployerAllocations),
          },
          {
            path: "/employer-account-reports",
            element: render(EmployerAccountReports),
          },
          { path: "/employer-reports", element: render(EmployerReports) },
          { path: "/employer-documents", element: render(EmployerDocuments) },
          {
            path: "/employer-emails",
            element: withSuspense(<UserEmailMessages scope="employer" />),
          },
          {
            path: "/employer/employee-census",
            element: render(EmployeeCensus),
          },
          { path: "/employer/assignPartner", element: render(AssignPartner) },
          { path: "/employer/assignReferral", element: render(AssignReferral) },

          // Referrals
          { path: "/referrals-dashboard", element: render(MyReferrals) },
          { path: "/add-referral", element: render(AddReferral) },
          { path: "/add-referral/:referralId", element: render(AddReferral) },

          // Partner
          { path: "/partner-dashboard", element: render(PartnerDashboard) },
          { path: "/partner/licenses", element: render(PartnerLicenses) },
          { path: "/partner/documents", element: render(PartnerDocuments) },
          {
            path: "/partner/employee-insurance",
            element: render(PartnerEmployeeInsurance),
          },
          {
            path: "/partner/employee-enrollments",
            element: render(PartnerEmployeeEnrollment),
          },
          {
            path: "/partner/employee-enrollments/:purchaseInsuranceId",
            element: render(ManualEnrollment),
          },
          { path: "/partner/employees", element: render(PartnerEmployees) },
          {
            path: "/partner-onboarding/partner-profile",
            element: render(FlowPartner),
          },
          {
            path: "/partner-onboarding/documents",
            element: render(FlowPartnerDocuments),
          },
          { path: "/partner/admins", element: render(PartnerAdmins) },
          {
            path: "/partner/:partnerId/administrator/add",
            element: render(AddPartnerAdmin),
          },
          {
            path: "/partner/employee-onboarding/:userPlanId",
            element: render(PartnerEmployeeOnboarding),
          },
          {
            path: "/partner/health-insurance/:partnerEmployerUserId",
            element: render(HealthInsurance),
          },
          {
            path: "/partner/health-insurance/:partnerEmployerUserId/plan-details",
            element: render(HealthInsurancePlanDetails),
          },
          {
            path: "/partner/health-insurance/:partnerEmployerUserId/confirm",
            element: render(ConfirmHealthInsurance),
          },
          { path: "/change-email", element: render(ChangeEmail) },

          // Employers — admin only
          {
            element: guard((c) => c.showEmployers),
            children: [
              {
                path: "/employers-dashboard",
                element: render(EmployersDashboard),
              },
              {
                path: "/employer/close/:employerId",
                element: render(CloseEmployer),
              },
            ],
          },
          /**
           * ADDING AND EDITING AN EMPLOYER IS A PARTNER'S WORKFLOW TOO, so it
           * sits outside the admin group above. The partner dashboard offers
           * Add Employer as a primary action and every employer row's Edit
           * pencil points here, so guarding it on showEmployers locked
           * partners out of both.
           *
           * Listing every employer on the platform and closing one stay admin
           * only — neither is a partner's to do.
           */
          { path: "/employer/add/:employerId", element: render(AddEmployer) },

          // Users — admin only
          {
            element: guard((c) => c.showUsers),
            children: [
              { path: "/users-dashboard", element: render(UsersDashboard) },
              {
                path: "/data/hra-adjustment/add",
                element: render(HRAAdjustment),
              },
              { path: "/user-audit-log/:userId", element: render(AuditLog) },
            ],
          },
          // Conversations — admin only
          {
            element: guard((c) => c.showOps),
            children: [
              { path: "/conversations", element: render(Conversations) },
              {
                path: "/conversations/:conversationId",
                element: render(ConversationDetail),
              },
            ],
          },
          // Approvals — admin only
          {
            element: guard((c) => c.showOps),
            children: [
              {
                path: "/approval-expense",
                element: render(ExpenseApprovalV2),
              },
              {
                path: "/approval-insurance",
                element: render(InsuranceApproval),
              },
              {
                path: "/approval-text-image",
                element: render(TextImageVerification),
              },
              { path: "/approval-aitest", element: render(AiExpenseList) },
              { path: "/approval-ai-replies", element: render(AiMessageList) },
              {
                path: "/approval-ai-replies/:messageId",
                element: render(AiMessageDetail),
              },
              {
                path: "/approval-aitest/:sourceExpenseId",
                element: render(AiExpenseDetail),
              },
            ],
          },
          // Ops — admin only
          {
            element: guard((c) => c.showOps),
            children: [
              { path: "/ops-dashboard", element: render(DashboardData) },
              {
                path: "/ops-expense-approval",
                element: render(ExpenseApproval),
              },
              { path: "/ops-edit-expense", element: render(AdminEditExpense) },
              { path: "/ops-referrals", element: render(Referrals) },
              {
                path: "/ops-text-to-confirm-expense-approval",
                element: render(TextToConfirmExpenseApproval),
              },
              {
                path: "/ops-healthsherpa-policies",
                element: render(HealthSherpaPolicies),
              },
              {
                path: "/ops-employer-invoices",
                element: render(EmployerInvoices),
              },
              {
                path: "/ops-employer-release-hold",
                element: render(ReleaseHold),
              },
              {
                path: "/ops-employer-invoices/:employerId",
                element: render(EmployerInvoices),
              },
              {
                path: "/ops-svb-ach-transactions",
                element: render(SVBACHTransactions),
              },
              {
                path: "/ops-svb-ach-transactions/:employerId",
                element: render(SVBACHTransactions),
              },
              {
                path: "/ops-update-ach-transaction",
                element: render(UpdateACHTransaction),
              },
              {
                path: "/ops-reimbursement-account-balance",
                element: render(ReimbursementAccountBalance),
              },
              { path: "/ops-plan-templates", element: render(PlanTemplates) },
              { path: "/ops-email-templates", element: render(EmailTemplates) },
              {
                path: "/ops-message-templates",
                element: render(MessageTemplates),
              },
              {
                path: "/ops-message-template/add",
                element: render(MessageTemplateEdit),
              },
              {
                path: "/ops-message-template/edit/:id",
                element: render(MessageTemplateEdit),
              },
              { path: "/ops-manage-bank", element: render(ManageBankData) },
              {
                path: "/ops-combine-accounts",
                element: render(CombineAccounts),
              },
              {
                path: "/ops-change-employee-email",
                element: render(ChangeEmployeeEmail),
              },
              { path: "/ops-producers", element: render(Producers) },
              { path: "/ops-producers/producer", element: render(AddProducer) },
              {
                path: "/ops-producers/producer/:producerId",
                element: render(AddProducer),
              },
              { path: "/ops-producers/carrier", element: render(AddCarrier) },
              {
                path: "/ops-producers/carrier/:carrierId",
                element: render(AddCarrier),
              },
              { path: "/ops-producers/license", element: render(AddLicense) },
              {
                path: "/ops-producers/license/:licenseId",
                element: render(AddLicense),
              },
              {
                path: "/ops-producers/state-registration/",
                element: render(AddStateRegistration),
              },
              {
                path: "/ops-producers/state-registration/:stateRegistrationId",
                element: render(AddStateRegistration),
              },
              {
                path: "/ops-producers/state-filing",
                element: render(AddStateFiling),
              },
              {
                path: "/ops-producers/state-filing/:stateFilingId",
                element: render(AddStateFiling),
              },
              {
                path: "/ops-producers/appointment",
                element: render(AddAppointment),
              },
              {
                path: "/ops-producers/appointment/:appointmentId",
                element: render(AddAppointment),
              },
              { path: "/ops-producers/login", element: render(AddLogin) },
              {
                path: "/ops-producers/login/:loginId",
                element: render(AddLogin),
              },
              {
                path: "/ops-instantly-campaigns",
                element: render(InstantlyCampaigns),
              },
              { path: "/ops-instantly-leads", element: render(InstantlyLeads) },
              {
                path: "/ops-instantly-leads/:campaignId",
                element: render(InstantlyLeads),
              },
              {
                path: "/ops-instantly-employers",
                element: render(InstantlyEmployers),
              },
              {
                path: "/ops-instantly-employers/:employerId",
                element: render(InstantlyEmployers),
              },
              { path: "/ops-reviews", element: render(Reviews) },
              {
                path: "/ops-upload-health-sherpa-policies",
                element: render(UploadHealthSherpaPolicies),
              },
              {
                path: "/ops-get-health-sherpa-applications",
                element: render(GetHealthSherpaApplications),
              },
              {
                path: "/ops-get-health-sherpa-application/:purchaseInsuranceId",
                element: render(GetHealthSherpaApplication),
              },
            ],
          },

          // Usage — admin only
          {
            element: guard((c) => c.showUsage),
            children: [
              { path: "/usage-dashboard", element: render(MonthlyRevenue) },
              {
                path: "/usage-weekly-enrollment",
                element: render(WeeklyEnrollment),
              },
              {
                path: "/usage-channel-revenue",
                element: render(ChannelRevenue),
              },
              {
                path: "/usage-email-campaign-responses",
                element: render(EmailCampaignResponses),
              },
              { path: "/usage-ad-responses", element: render(AdResponses) },
              { path: "/usage-calculators", element: render(Calculators) },
              {
                path: "/usage-health-sherpa",
                element: render(HealthSherpaUsage),
              },
              { path: "/usage-ad-clicks", element: render(AdClicks) },
              {
                path: "/usage-ip-address-summary",
                element: render(IPAddressSummary),
              },
              {
                path: "/usage-learning-center",
                element: render(LearningCenter),
              },
              {
                path: "/usage-public-visitor-history",
                element: render(PublicVisitorHistory),
              },
            ],
          },

          // Data — developer only
          {
            element: guard((c) => c.showData),
            children: [
              { path: "/data-dashboard", element: render(SearchData) },
              { path: "/data-search", element: render(SearchData) },
              { path: "/data-partners", element: render(PartnersData) },
              { path: "/data-expenses", element: render(ExpensesData) },
              {
                path: "/data-expenses/:employerId",
                element: render(ExpensesData),
              },
              {
                path: "/data-text-to-confirm-premiums",
                element: render(TextToConfirmPremiumsData),
              },
              {
                path: "/data-expense-texts",
                element: render(ExpenseTextsData),
              },
              {
                path: "/data-text-messages",
                element: render(TextMessagesData),
              },
              {
                path: "/data-text-responses",
                element: render(TextResponsesData),
              },
              { path: "/data-claims", element: render(ClaimsData) },
              { path: "/data-transactions", element: render(TransactionsData) },
              {
                path: "/data-transactions/:employerId",
                element: render(TransactionsData),
              },
              { path: "/data-employer-plans", element: render(EmployerPlans) },
              { path: "/data-all-banks", element: render(AllBanksData) },
              {
                path: "/data-nacha-transactions",
                element: render(NachaTransactionsData),
              },
              {
                path: "/data-nacha-transactions/:employerId",
                element: render(NachaTransactionsData),
              },
              {
                path: "/data-health-sherpa-webhooks",
                element: render(HealthSherpaWebhooksData),
              },
            ],
          },

          // System — developer only
          {
            element: guard((c) => c.showSystem),
            children: [
              { path: "/system-dashboard", element: render(CronTasks) },
              { path: "/system-logs", element: render(SystemLogs) },
            ],
          },

          // Employer and user summary pages — not admin, so not guarded
          {
            path: "/administrator-summary",
            element: render(AdministratorSummary),
          },
          { path: "/employee-summary", element: render(EmployeeSummary) },
          { path: "/premium-summary", element: render(PremiumSummary) },
          {
            path: "/billing-history-summary",
            element: render(BillingHistorySummary),
          },
          { path: "/report-summary", element: render(ReportSummary) },
          { path: "/message-summary", element: render(MessageSummary) },
          // { path: "/ttc-premium-summary", element: render(TTCPremiumSummary) },
        ],
      },
      // Catch-all route for 404 errors
      { path: "*", element: render(ErrorPage) },
    ],
  },
]);

function App() {
  useEffect(() => {
    // Chunk load error handler — auto-reload on new deployment
    function handleChunkError(e) {
      if (e.message?.includes("Loading chunk")) {
        window.location.reload();
      }
    }
    window.addEventListener("error", handleChunkError);

    // Existing toast dismiss handler
    function handleClickOutside(event) {
      const toasts = document.querySelectorAll(".Toastify__toast");
      const toastContainer = document.querySelector(
        ".Toastify__toast-container",
      );

      let isClickInsideToast = false;
      if (toastContainer && toastContainer.contains(event.target)) {
        isClickInsideToast = true;
      }
      toasts.forEach((toastElement) => {
        if (toastElement.contains(event.target)) {
          isClickInsideToast = true;
        }
      });

      if (!isClickInsideToast && toasts.length > 0) {
        toast.dismiss();
      }
    }
    document.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("error", handleChunkError);
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // useEffect(() => {
  //   function handleClickOutside(event) {
  //     const toasts = document.querySelectorAll('.Toastify__toast');
  //     const toastContainer = document.querySelector('.Toastify__toast-container');

  //     // Check if click is inside any toast or toast container
  //     let isClickInsideToast = false;

  //     if (toastContainer && toastContainer.contains(event.target)) {
  //       isClickInsideToast = true;
  //     }

  //     toasts.forEach((toastElement) => {
  //       if (toastElement.contains(event.target)) {
  //         isClickInsideToast = true;
  //       }
  //     });

  //     // Only dismiss if clicking outside toast area
  //     if (!isClickInsideToast && toasts.length > 0) {
  //       toast.dismiss();
  //     }
  //   }

  //   document.addEventListener('click', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('click', handleClickOutside);
  //   };
  // }, []);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box>
          <UserProvider>
            <RouterProvider router={router} />
            <ToastContainer
              position="top-center"
              autoClose={0}
              hideProgressBar={true}
              newestOnTop
              closeOnClick
              pauseOnHover
            />
          </UserProvider>
        </Box>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
